import { useCallback, useEffect, useMemo, useState } from 'react'

import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { toAppointmentSeriesLink } from 'features/Series/api/series.transform'
import { SHOW_SERIES } from 'features/Series/series.flags'
import { clearSeriesTab, peekSeriesTab } from 'features/Series/seriesTabIntent'

import { openingKeyFor } from '../useDialogMode'
import type { CreateSeed } from './create/seriesCreate.model'
import { type SeriesCreate, useSeriesCreate } from './create/useSeriesCreate'
import { type SeriesEdit, useSeriesEdit } from './edit/useSeriesEdit'
import type { SeriesFooter } from './series.types'
import { peopleOf, type VisitPeople } from './seriesPeople'
import { SERIES_TEXT_V2 } from './seriesText'

export type SeriesTabName = 'single' | 'series'

type Args = {
  isEmr: boolean
  isEditing: boolean
  openingKey: string
  appointment: IPatientSchedule | null
  siteId: string
  patientId: number | null
  primaryDoctorId: number | null
  categoryId: number | null
  // The Series people are picked here, so the footer's name is looked up here too.
  providerNameOf: (doctorId: number) => string
  seed: CreateSeed
  onCategoryFromTemplate: (id: number) => void
}

export type SeriesTab = {
  visible: boolean
  switchDisabled: boolean
  isSeries: boolean
  note: string
  setTab: (tab: SeriesTabName) => void
  seriesPeople: VisitPeople | null
  // null empties the band: Save stays off until a provider is picked again.
  setSeriesPeople: (next: VisitPeople | null) => void
  create: SeriesCreate | null
  edit: SeriesEdit | null
  // Who the pending edit takes off booked visits; empty outside an edit.
  removal: SeriesRemoval
  footer: SeriesFooter | null
  canSave: boolean
  isSaving: boolean
  save: () => Promise<boolean>
}

// Names for the prompt; key is per person and visits, so a new visit asks again.
export type SeriesRemoval = { removedNames: string[]; warning: string; key: string }

// One opening: its tab, its Series people, and a generation that makes every opening's draft key unique.
type Opening = {
  key: string
  generation: number
  tab: SeriesTabName
  // Until the band is touched it follows the primary doctor.
  peoplePicked: boolean
  people: VisitPeople | null
}

const CLOSED = openingKeyFor(false)

// Shown while an edit's run is loading or failed; nothing to save yet.
const PENDING_FOOTER: SeriesFooter = {
  kicker: SERIES_TEXT_V2.kickerSeries,
  title: '',
  subtitle: '',
  tone: 'muted',
  saveLabel: SERIES_TEXT_V2.save,
}

const requestedTab = (key: string, appointment: IPatientSchedule | null): SeriesTabName =>
  key !== CLOSED && appointment?.id !== undefined && peekSeriesTab(appointment.id)
    ? 'series'
    : 'single'

const noteOf = (isEditing: boolean, hasLink: boolean, isSeries: boolean): string => {
  if (isEditing)
    return hasLink ? SERIES_TEXT_V2.modeNoteEditSeries : SERIES_TEXT_V2.modeNoteEditOrdinary
  return isSeries ? SERIES_TEXT_V2.modeNoteCreateSeries : SERIES_TEXT_V2.modeNoteCreateSingle
}

export const useSeriesTab = ({
  isEmr,
  isEditing,
  openingKey,
  appointment,
  siteId,
  patientId,
  primaryDoctorId,
  categoryId,
  providerNameOf,
  seed,
  onCategoryFromTemplate,
}: Args): SeriesTab => {
  const [latched, setLatched] = useState<Opening>(() => ({
    key: openingKey,
    generation: 0,
    tab: requestedTab(openingKey, appointment),
    peoplePicked: false,
    people: null,
  }))

  // A new opening starts on the requested tab, with fresh drafts, in the same render.
  const opening: Opening =
    latched.key === openingKey
      ? latched
      : {
          key: openingKey,
          generation: latched.generation + 1,
          tab: requestedTab(openingKey, appointment),
          peoplePicked: false,
          people: null,
        }
  if (opening !== latched) setLatched(opening)

  // The request is read during render and used once.
  useEffect(() => {
    if (openingKey !== CLOSED) clearSeriesTab()
  }, [openingKey])

  const link = useMemo(
    () => (isEditing ? toAppointmentSeriesLink(appointment?.series) : null),
    [isEditing, appointment?.series],
  )
  const visible = SHOW_SERIES && isEmr
  const switchDisabled = isEditing && link === null
  const isSeries = visible && !switchDisabled && opening.tab === 'series'
  const resetKey = `${openingKey}#${opening.generation}`
  const defaultBand = useMemo(
    () => peopleOf(primaryDoctorId === null ? [] : [primaryDoctorId]),
    [primaryDoctorId],
  )
  const seriesPeople = opening.peoplePicked ? opening.people : defaultBand

  const isCreating = isSeries && !isEditing
  // No site, no templates request: nothing is fetched until a create shows the Series tab.
  const createHook = useSeriesCreate({
    siteId: isCreating ? siteId : '',
    resetKey,
    active: isCreating,
    patientId,
    band: seriesPeople,
    categoryId,
    providerName: seriesPeople
      ? SERIES_TEXT_V2.providerSummary(
          providerNameOf(seriesPeople.primaryId),
          seriesPeople.people.length - 1,
        )
      : '',
    seed,
    onCategoryFromTemplate,
  })
  const editHook = useSeriesEdit({
    siteId,
    resetKey,
    seriesId: link?.id ?? null,
    openedStep: link?.step ?? null,
    openedDoctorId: appointment?.consultation_staff ?? null,
    active: isSeries && link !== null,
  })
  const create = visible && !isEditing ? createHook : null
  const edit = visible && link !== null ? editHook : null

  const setTab = useCallback(
    (tab: SeriesTabName) =>
      setLatched((current) =>
        current.key === openingKey && !(tab === 'series' && switchDisabled)
          ? { ...current, tab }
          : current,
      ),
    [openingKey, switchDisabled],
  )

  const setSeriesPeople = useCallback(
    (next: VisitPeople | null) =>
      setLatched((current) =>
        current.key === openingKey ? { ...current, peoplePicked: true, people: next } : current,
      ),
    [openingKey],
  )

  const save = async (): Promise<boolean> => {
    if (!isSeries) return false
    if (create) return create.save()
    if (edit) return (await edit.save()) === 'closed'
    return false
  }

  const removals = edit?.removals ?? []
  const removal: SeriesRemoval = {
    removedNames: removals.map((r) => r.name),
    warning: removals.length ? SERIES_TEXT_V2.seriesRemoval(removals) : '',
    key: removals.map((r) => `${r.id}:${r.visits.join(',')}`).join('|'),
  }

  const editReady = edit?.status === 'ready'
  return {
    visible,
    switchDisabled,
    isSeries,
    note: noteOf(isEditing, link !== null, isSeries),
    setTab,
    seriesPeople,
    setSeriesPeople,
    create,
    edit,
    removal,
    footer: isSeries ? create?.footer ?? edit?.footer ?? PENDING_FOOTER : null,
    canSave:
      isSeries &&
      (create ? create.canSave && seriesPeople !== null : editReady && Boolean(edit?.canSave)),
    isSaving: Boolean(create?.isSaving || edit?.isSaving),
    save,
  }
}
